/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import styled from 'styled-components';
import { Switch, Route } from 'react-router-dom';
import Responsive from 'react-responsive';

import SubscriptionsTable from './components/SubscriptionsTable';
import Header from './components/Header';
import Localized from '../../../shared/components/Localized';
import Toast from '../../components/Toast';
import { UtilityIconButton } from '../../components/SVGIcon';
import styles from '../../styles';
import NewSubscriptionModal from './components/NewSubscriptionModal';
import EditInstanceSubscriptionModal from './components/EditInstanceSubscriptionModal';
import DeleteInstanceSubscriptionModal from './components/DeleteInstanceSubscriptionModal';
import EditAllSubscriptionModal from './components/EditAllSubscriptionModal';
import DeleteAllSubscriptionModal from './components/DeleteAllSubscriptionModal';
import EditGeneralMessageSubscriptionModal from './components/EditGeneralMessageSubscriptionModal';
import DeleteGeneralMessageSubscriptionModal from './components/DeleteGeneralMessageSubscriptionModal';
import LoadingSpinner from '../../components/Spinners/LoadingSpinner';
import UnsubscribeModal from './components/UnsubscribeModal';
import SubscriptionsSidePanel from './components/SubscriptionsSidePanel';
import Tabs from '../../components/Tabs';

import RemovePhoneNumberModal from './components/RemovePhoneNumberModal';
import ModalBody from './components/ModalBody';

const Wrapper = styled.div`
  @media (min-width: 768px) {
    ${p => !p.fullscreen && 'padding: 0 0.75rem 0 0.75rem;'}
  }
`;

const Pane = styled.div`
  height: 100%;
  position: relative;
  background-color: white;
  margin-bottom: 1rem;
  ${styles.borders.card}
  ${p => p.fullscreen && 'border-radius: 0;'}
  ${p => p.fullscreen && 'box-shadow: none;'}

  @media (max-width: 767px) {
    border-radius: 0;
  }
`;

const SubscriptionsLeftPanel = styled.div`
  min-width: 20%;
  border-right: 1px solid #d8dde6;
`;

const SubscriptionsRightPanel = styled.div`
  min-width: 80%;
`;

const DesktopIconSpan = styled.span`
  float: right;
  > * {
    margin-right: 1rem;
  }
`;

const MobileIconSpan = styled.span`
  float: left;
`;

const Container = styled.div`
  display: flex;
`;

const ActionButton = styled.span`
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.54;
  color: #006dcc;
  padding-left: 1rem;
  cursor: pointer;
`;

const SubscriptionTabs = styled(Tabs)`
  width: 100%;
  height: 64px;
  box-shadow: inset 0 -2px 0 0 #d8dde6;
  background-color: #ffffff;
`;

const SubscriptionUtilityIcon = styled(UtilityIconButton)`
  margin-right: 1rem;
`;

const Desktop = props => <Responsive {...props} minWidth={768} />;
const Mobile = props => <Responsive {...props} maxWidth={767} />;

class Subscriptions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      modalOpen: undefined,
      key: undefined,
      isProduct: false,
      selectedLocale: undefined,
      changingLocale: false,
      toast: { state: undefined, message: '' },
      // New State for keeping track of email subscriptions
      emailSubscribed: false,
      smsSubscribed: false,
    };
    this.onSubscribeClick = this.onSubscribeClick.bind(this);
    this.onEditClick = this.onEditClick.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.onUnsubscribeClick = this.onUnsubscribeClick.bind(this);
    this.onLocaleChange = this.onLocaleChange.bind(this);
    this.showToast = this.showToast.bind(this);
    this.onModalClose = this.onModalClose.bind(this);
    this.onToastClose = this.onToastClose.bind(this);

    this.onEmailSubscribeToggle = this.onEmailSubscribeToggle.bind(this);
    this.onSmsSubscribeToggle = this.onSmsSubscribeToggle.bind(this);
    this.onPhoneNumberAdd = this.onPhoneNumberAdd.bind(this);
    this.onPhoneNumberRemove = this.onPhoneNumberRemove.bind(this);
    this.onPhoneNumberVerify = this.onPhoneNumberVerify.bind(this);

    this.columns = [
      {
        title: <Localized modelKey="subscribeTo" modelAttribute="header" modelName="subscriptions" />,
        name: 'instanceKey',
        render: row => (
          <Localized
            modelKey={row.instanceKey}
            modelAttribute="label"
            modelName={row.isProduct ? 'product' : 'instance'}
          />
        ),
        key: true,
      },
      {
        title: <Localized modelKey="email" modelAttribute="label" modelName="subscribeModal" />,
        name: 'email',
        render: row => (
          <Localized
            modelKey={row.emailSub ? row.emailSub : 'all'}
            modelAttribute="tabTitle"
            modelName="InstanceLocation"
          />
        ),
      },
      {
        title: <Localized modelKey="sms" modelAttribute="header" modelName="subscriptions" />,
        name: 'sms',
        render: row => (
          <Localized
            modelKey={row.smsSub ? row.smsSub : 'all'}
            modelAttribute="tabTitle"
            modelName="InstanceLocation"
          />
        ),
      },
      {
        title: '',
        name: 'actions',
        render: row => (
          <div>
            <Desktop>
              <DesktopIconSpan>
                <ActionButton onClick={() => this.onEditClick(row.instanceKey, row.isProduct, row.isGeneralMessage)}>
                  {'Edit'}
                </ActionButton>
                <ActionButton onClick={() => this.onDeleteClick(row.instanceKey, row.isProduct, row.isGeneralMessage)}>
                  {'Remove'}
                </ActionButton>
              </DesktopIconSpan>
            </Desktop>
            <Mobile>
              <MobileIconSpan>
                <SubscriptionUtilityIcon
                  icon="edit"
                  size="1rem"
                  onClick={() => this.onEditClick(row.instanceKey, row.isProduct, row.isGeneralMessage)}
                />
                <SubscriptionUtilityIcon
                  icon="delete"
                  size="1rem"
                  onClick={() => this.onDeleteClick(row.instanceKey, row.isProduct, row.isGeneralMessage)}
                />
              </MobileIconSpan>
            </Mobile>
          </div>
        ),
      },
    ];
  }

  componentDidMount() {
    const { token, history, requests } = this.props;
    // If no token is set, redirect to login page
    if (!token) {
      history.push('/login');
    } else {
      requests.subscriber.handleRequest().then(res => {
        // if no response, there was an error due to invalid token
        if (!res) history.push('/login');
        else {
          history.push('/subscriptions');
        }
      });
    }
  }

  onSubscribeClick() {
    this.setState({ modal: 'new' });
  }

  onUnsubscribeClick() {
    this.setState({ modal: 'unsubscribe' });
  }

  onLocaleChange(locale) {
    this.setState({ selectedLocale: locale, changingLocale: true });
    this.props.requests.patchSubscriber
      .handleRequest({
        body: {
          locale,
        },
      })
      .then(() => {
        return this.props.requests.subscriber.handleRequest();
      })
      .then(() => {
        this.setState({ selectedLocale: locale, changingLocale: false });
        this.showToast('success', 'Your locale has been successfully updated!');
      })
      .catch(() => {
        this.setState({ selectedLocale: undefined, changingLocale: false });
        this.showToast('failed', 'There was an error. Please try again');
      });
  }

  onEditClick(key, isProduct, isGeneralMessage) {
    if (key === 'ALL' || isProduct) {
      this.setState({ modal: 'editAll', key: key, isProduct: true });
    } else if (isGeneralMessage) {
      this.setState({ modal: 'editGeneralMessage', key: 'generalMessage', isProduct: false });
    } else {
      this.setState({ modal: 'editInstance', key: key, isProduct: false });
    }
  }

  onDeleteClick(key, isProduct, isGeneralMessage) {
    if (key === 'ALL') {
      this.setState({ modal: 'deleteAll', isProduct: isProduct });
    } else if (isGeneralMessage) {
      this.setState({ modal: 'deleteGeneralMessage', key: 'generalMessage', isProduct: false });
    } else {
      this.setState({ modal: 'deleteInstance', key: key, isProduct: isProduct });
    }
  }

  onModalClose(state, message) {
    this.setState({ modal: undefined, key: undefined });
    if (typeof state === 'string' && message) {
      this.showToast(state, message);
    }
  }

  showToast(state, message) {
    this.setState({ toast: { state, message } });
    setTimeout(() => {
      this.onToastClose();
    }, 3 * 1000);
  }

  onToastClose() {
    this.setState({ toast: { state: undefined, message: '' } });
  }
  // Modals for email and phone number handling:
  onEmailSubscribeToggle(val) {
    const emailSubscribed = val;
    this.props.requests.patchSubscriber
      .handleRequest({
        body: {
          emailSubscribed,
        },
      })
      .then(() => {
        this.setState({ emailSubscribed });
        return this.props.requests.subscriber.handleRequest();
      })
      .then(subscriber => {
        if (subscriber.emailSubscribed) {
          this.showToast('success', 'You have subscribed to receive Email notifications!');
        } else {
          this.showToast('success', 'You will no longer receive Email notifications.');
        }
      })
      .catch(() => {
        this.showToast('failed', 'There was an error. Please try again');
      });
  }

  onSmsSubscribeToggle(val) {
    const smsSubscribed = val;
    const { phoneNumber, mobileNumberVerified } = this.props.requests.subscriber.value;
    if (!phoneNumber) {
      this.showToast('failure', 'Please add a phone number first');
    } else if (!mobileNumberVerified) {
      this.showToast('failure', 'Please verify your phone number first');
    } else {
      this.props.requests.patchSubscriber
        .handleRequest({
          body: {
            smsSubscribed,
          },
        })
        .then(() => {
          this.setState({ smsSubscribed });
          return this.props.requests.subscriber.handleRequest();
        })
        .then(subscriber => {
          if (subscriber.smsSubscribed) {
            this.showToast('success', 'You have subscribed to receive SMS notifications!');
          } else {
            this.showToast('success', 'You will no longer receive SMS notifications.');
          }
        })
        .catch(() => {
          this.showToast('failure', 'There was an error. Please try again');
        });
    }
  }

  onPhoneNumberAdd() {
    this.setState({ modal: 'addPhone' });
  }

  onPhoneNumberRemove() {
    this.setState({ modal: 'removePhone' });
  }

  onPhoneNumberVerify() {
    this.setState({ modal: 'verifyPhone' });
  }

  render() {
    const { requests, fullscreen, localePicker } = this.props;

    if (!requests.subscriber.isFirstCompleted || !requests.locales.isCompleted || !requests.instanceList.isCompleted)
      return <LoadingSpinner inverse />;
    const subscriber = requests.subscriber.value;
    const locales = requests.locales.value.map(l => ({
      value: l.code,
      text: l.name,
    }));

    const groupBy = key => array =>
      array.reduce((objectsByKeyValue, obj) => {
        const value = obj[key];
        objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
        return objectsByKeyValue;
      }, {});

    const instances = requests.instanceList.value || [];
    const productRows = subscriber.Subscriptions.filter(sub => sub.productKey)
      .map(sub => {
        const all = sub.major && sub.minor && sub.maintenance;

        return {
          instanceKey: sub.productKey,
          type: all ? 'All' : 'Custom',
          isProduct: true,
        };
      })
      .sort(alphanumSpecial);
    const returnSubCustomization = sub => {
      if (sub.allNotifications) {
        return 'ALL';
      } else {
        if (sub.ServiceSubscriptions.length === 0) {
          return 'None';
        } else {
          return 'Custom';
        }
      }
    };

    const activeInstances = instances.map(a => a.key);
    const instanceRows = [];
    subscriber.Subscriptions.forEach(function (sub) {
      if (activeInstances.includes(sub.instanceKey)) {
        instanceRows.push( {
          instanceKey: sub.instanceKey,
          type: returnSubCustomization(sub),
          isProduct: false,
          subType: sub.subType,
        }
       )
      }
  });
   instanceRows.sort(alphanumSpecial);
    let arrayGroupings = [];
    const groupByInstanceKey = groupBy('instanceKey');
    const subscriptions = groupByInstanceKey(instanceRows);
    let x;
    for (x of Object.keys(subscriptions)) {
      let emailType = 'None';
      let smsType = 'None';
      subscriptions[x].map(subscriptionRow => {
        if (subscriptionRow.subType === 'email' && subscriptionRow.type === 'ALL') emailType = 'All';
        else if (subscriptionRow.subType === 'email' && subscriptionRow.type === 'Custom') emailType = 'Custom';
        else if (subscriptionRow.subType === 'email' && subscriptionRow.type === 'None') emailType = 'None';
        if (subscriptionRow.subType === 'sms' && subscriptionRow.type === 'ALL') smsType = 'All';
        else if (subscriptionRow.subType === 'sms' && subscriptionRow.type === 'Custom') smsType = 'Custom';
        else if (subscriptionRow.subType === 'sms' && subscriptionRow.type === 'None') smsType = 'None';
      });
      let retval = {
        instanceKey: x,
        emailSub: emailType,
        smsSub: smsType,
      };
      arrayGroupings.push(retval);
    }

    const { generalMessageEmailSubscribed, generalMessageSMSSubscribed } = subscriber;
    if (generalMessageEmailSubscribed || generalMessageSMSSubscribed) {
      arrayGroupings.push({
        instanceKey: 'Informational Message',
        emailSub: generalMessageEmailSubscribed ? 'All' : 'None',
        smsSub: generalMessageSMSSubscribed ? 'All' : 'None',
        isGeneralMessage: true,
      });
    }

    let subscription;
    if (this.state.modal === 'editInstance' || this.state.modal === 'deleteInstance') {
      subscription = subscriber.Subscriptions.filter(s => {
        return s.instanceKey === this.state.key;
      });
    } else if (this.state.modal === 'editAll' || this.state.modal === 'deleteAll') {
      if (this.state.isProduct) {
        subscription = subscriber.Subscriptions.find(s => {
          return s.productKey === this.state.key;
        });
      } else {
        // is instance
        subscription = subscriber.Subscriptions.filter(s => s.instanceKey === this.state.key);
      }
    }
    const selectedLocale = this.state.changingLocale ? this.state.selectedLocale : subscriber.locale;
    const noneTextProduct = (
      <Localized modelKey="noProductSubscription" modelAttribute="label" modelName="subscriptions" />
    );
    const noneTextInstance = (
      <Localized modelKey="noInstanceSubscription" modelAttribute="label" modelName="subscriptions" />
    );

    const tabs = [
      {
        name: 'Subscriptions',
        url: this.props.match.url,
        to: this.props.match.url,
      },
      {
        name: 'Settings',
        url: `${this.props.match.url}/settings`,
      },
    ];

    const subscriptionSettings = (
      <SubscriptionsSidePanel
        heading={'settings'}
        email={subscriber.emailAddress}
        phone={subscriber.phoneNumber}
        onLocaleChange={this.onLocaleChange}
        selectedLocale={localePicker}
        locales={locales}
        mobileNumberVerified={subscriber.mobileNumberVerified}
        emailVerified={subscriber.emailVerified}
        onPhoneNumberAdd={this.onPhoneNumberAdd}
        onPhoneNumberRemove={this.onPhoneNumberRemove}
        onPhoneNumberVerify={this.onPhoneNumberVerify}
      />
    );

    const subscriptionDetails = (
      <div>
        <SubscriptionsTable
          rows={arrayGroupings}
          columns={this.columns}
          showColumns={true}
          hasActionRow={true}
          onEmailSubscribeToggle={this.onEmailSubscribeToggle}
          onSmsSubscribeToggle={this.onSmsSubscribeToggle}
          emailSubscribed={subscriber.emailSubscribed}
          smsSubscribed={subscriber.smsSubscribed}
          noneText={noneTextInstance}
        />
        {productRows.length !== 0 && (
          <SubscriptionsTable rows={productRows} columns={this.columns} noneText={noneTextProduct} />
        )}
      </div>
    );

    return (
      <Wrapper fullscreen={fullscreen}>
        {this.state.modal === 'new' && (
          <NewSubscriptionModal
            subscriber={subscriber}
            products={requests.products.value}
            onClose={this.onModalClose}
          />
        )}
        {this.state.modal === 'editInstance' && (
          <EditInstanceSubscriptionModal
            instances={instances}
            instanceSubscription={subscription}
            onClose={this.onModalClose}
          />
        )}
        {this.state.modal === 'editGeneralMessage' && (
          <EditGeneralMessageSubscriptionModal onClose={this.onModalClose} />
        )}
        {this.state.modal === 'deleteGeneralMessage' && (
          <DeleteGeneralMessageSubscriptionModal onClose={this.onModalClose} />
        )}
        {this.state.modal === 'editAll' && (
          <EditAllSubscriptionModal
            subscriberAddress={subscriber.emailAddress}
            label={this.state.isProduct ? 'PRODUCT' : ''}
            valueText={this.state.isProduct ? this.state.key : ''}
            tableLabel={this.state.isProduct ? 'CHOOSE IMPACT(S):' : ''}
            instances={this.state.isProduct ? instances : []}
            subscription={subscription}
            onClose={this.onModalClose}
          />
        )}
        {this.state.modal === 'deleteInstance' && (
          <DeleteInstanceSubscriptionModal
            instanceSubscription={subscription}
            onClose={this.onModalClose}
            isProduct={this.state.isProduct}
          />
        )}
        {this.state.modal === 'deleteAll' && (
          <DeleteAllSubscriptionModal subscription={subscription} onClose={this.onModalClose} />
        )}
        {this.state.modal === 'unsubscribe' && <UnsubscribeModal onClose={this.onModalClose} />}

        {this.state.modal === 'addPhone' && <ModalBody modal={'addPhone'} onClose={this.onModalClose} />}
        {this.state.modal === 'removePhone' && <RemovePhoneNumberModal onClose={this.onModalClose} />}
        {this.state.modal === 'verifyPhone' && (
          <ModalBody modal={'verification'} subscriber={subscriber} onClose={this.onModalClose} />
        )}
        {this.state.modal === 'successVerifyPhone' && (
          <ModalBody modal={'successVerification'} subscriber={subscriber} onClose={this.onModalClose} />
        )}

        {!fullscreen}
        <Pane fullscreen={fullscreen}>
          {this.state.toast.state && (
            <Toast theme={this.state.toast.state} onClose={this.onToastClose}>
              {this.state.toast.message}
            </Toast>
          )}
          <Header
            subscriber={subscriber}
            locales={locales}
            onSubscribeClick={this.onSubscribeClick}
            onUnsubscribeClick={this.onUnsubscribeClick}
            onLocaleChange={this.onLocaleChange}
            selectedLocale={selectedLocale}
            changingLocale={this.state.changingLocale}
            logout={this.props.logout}
          />
          <Desktop>
            <Container>
              <SubscriptionsLeftPanel>{subscriptionSettings}</SubscriptionsLeftPanel>
              <SubscriptionsRightPanel>{subscriptionDetails}</SubscriptionsRightPanel>
            </Container>
          </Desktop>
          <Mobile>
            <SubscriptionTabs items={tabs} />
            <Switch>
              <Route path={`${this.props.match.path}/settings`}>{subscriptionSettings}</Route>
              <Route path={`${this.props.match.path}`}>{subscriptionDetails}</Route>
            </Switch>
          </Mobile>
        </Pane>
      </Wrapper>
    );
  }
}

function alphanumSpecial(a, b) {
  // numeric regex
  const numberOrNotRegex = /\d+|\D+/g;
  const c = a.instanceKey;
  const d = b.instanceKey;

  if (c === 'ALL') return -1;
  if (d === 'ALL') return 1;

  // chunk by digit or not digit
  const aChunks = c.match(numberOrNotRegex);
  const bChunks = d.match(numberOrNotRegex);

  // compare each chunk

  for (let x = 0; aChunks[x] && bChunks[x]; x++) {
    // skip if the same

    if (aChunks[x] === bChunks[x]) continue;

    // cast chunks to numbers
    const aNumber = Number(aChunks[x]);
    const bNumber = Number(bChunks[x]);

    // if values were unchanged during casting, then values are numbers
    // test using weak compare

    if (aNumber == aChunks[x] && bNumber == bChunks[x]) {
      return aNumber - bNumber;
    }

    // else use simple compare

    return aChunks[x] > bChunks[x] ? 1 : -1;
  }

  // if end of chunks reached compare by number of chunks
  return aChunks.length - bChunks.lenght;
}

export default Subscriptions;
